import { useEffect, useRef, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const CLOSE_MS=180;

export function OverlayPortal({children}:{children:ReactNode}){
  const closing=useRef(false);
  const replaying=useRef(false);

  const animateClose=(backdrop:HTMLElement,replay:()=>void)=>{
    if(closing.current||replaying.current)return;
    const close=backdrop.querySelector<HTMLButtonElement>('.drawerClose');
    if(close?.disabled)return;
    closing.current=true;
    backdrop.classList.add('closing');
    backdrop.querySelector<HTMLElement>('.drawer')?.classList.add('closing');
    window.setTimeout(()=>{
      replaying.current=true;
      replay();
    },CLOSE_MS);
  };

  const onClickCapture=(event:ReactMouseEvent<HTMLDivElement>)=>{
    if(replaying.current)return;
    const target=event.target as HTMLElement;
    const button=target.closest<HTMLButtonElement>('.drawerClose');
    if(!button||button.disabled)return;
    const backdrop=button.closest<HTMLElement>('.drawerBackdrop');
    if(!backdrop)return;
    event.preventDefault();
    event.stopPropagation();
    animateClose(backdrop,()=>button.click());
  };

  const onMouseDownCapture=(event:ReactMouseEvent<HTMLDivElement>)=>{
    if(replaying.current)return;
    const target=event.target as HTMLElement;
    if(!target.classList.contains('drawerBackdrop'))return;
    const close=target.querySelector<HTMLButtonElement>('.drawerClose');
    if(close?.disabled)return;
    event.preventDefault();
    event.stopPropagation();
    animateClose(target,()=>target.dispatchEvent(new MouseEvent('mousedown',{bubbles:true,cancelable:true})));
  };

  useEffect(()=>{
    if(typeof document==='undefined')return;
    const onKeyDown=(event:KeyboardEvent)=>{
      if(event.key!=='Escape')return;
      const drawers=Array.from(document.querySelectorAll<HTMLElement>('.drawerBackdrop'));
      const top=drawers.at(-1);
      const close=top?.querySelector<HTMLButtonElement>('.drawerClose:not(:disabled)');
      if(close){event.preventDefault();close.click();}
    };
    document.addEventListener('keydown',onKeyDown);
    return ()=>document.removeEventListener('keydown',onKeyDown);
  },[]);

  if(typeof document==='undefined')return <>{children}</>;
  return createPortal(<div className="overlayPortalHost" onClickCapture={onClickCapture} onMouseDownCapture={onMouseDownCapture}>{children}</div>,document.body);
}
